/**
 * ============================================================
 * HOBBLIVISION PARSER
 * ============================================================
 *
 * Parser V4.8
 *
 * PURPOSE
 * -------
 * Convert noisy OCR into structured collectible/card clues.
 *
 * Parser responsibilities:
 *
 *   - preserve raw OCR
 *   - normalize OCR
 *   - apply conservative OCR corrections
 *   - identify card/set numbers
 *   - identify set/manufacturer clues
 *   - identify likely player/name candidates
 *   - identify years
 *   - identify variation/insert clues
 *   - aggregate repeated OCR observations
 *   - rank identity candidates
 *   - expose diagnostics
 *
 * The parser DOES NOT:
 *
 *   - search the database
 *   - search Google
 *   - decide the final card
 *
 * The matcher will use this structured information later.
 *
 * ============================================================
 */

const PARSER_VERSION = "4.8";

/**
 * ============================================================
 * CONFIGURATION / KNOWLEDGE
 * ============================================================
 *
 * These are deliberately data-driven.
 *
 * Today they are local seed rules.
 *
 * Later these can move into database tables such as:
 *
 *   parser_rules
 *   card_sets
 *   players
 *   manufacturers
 *   variations
 *   ocr_aliases
 *
 * The parser itself should not need to be rewritten when
 * additional knowledge is added.
 * ============================================================
 */

const KNOWLEDGE = {
  /**
   * --------------------------------------------------------
   * Known sets
   * --------------------------------------------------------
   */

  sets: [
    {
      canonical: "T206",

      aliases: ["T206", "T 206", "T7206", "T 7206", "7206"],

      confidence: 0.95,
    },

    {
      canonical: "ALLEN & GINTER",

      aliases: [
        "ALLEN & GINTER",
        "ALLEN AND GINTER",
        "ALLEN  GINTER",
        "ALLEN & GINTEI",
      ],

      confidence: 0.94,
    },

    {
      canonical: "OLD MILL",
      aliases: ["OLD MILL"],
      confidence: 0.8,
    },

    {
      canonical: "TOPPS",
      aliases: ["TOPPS"],
      confidence: 0.8,
    },

    {
      canonical: "BOWMAN",
      aliases: ["BOWMAN"],
      confidence: 0.8,
    },

    {
      canonical: "DONRUSS",
      aliases: ["DONRUSS"],
      confidence: 0.8,
    },

    {
      canonical: "FLEER",
      aliases: ["FLEER"],
      confidence: 0.8,
    },

    {
      canonical: "UPPER DECK",
      aliases: ["UPPER DECK"],
      confidence: 0.8,
    },

    {
      canonical: "HOOPS",
      aliases: ["HOOPS"],
      confidence: 0.8,
    },
  ],

  /**
   * --------------------------------------------------------
   * Variation terminology
   * --------------------------------------------------------
   */

  variationTerms: [
    "PORTRAIT",
    "PORTRAIT-RED",
    "RED PORTRAIT",
    "CAPORAL",
    "S.CAPORAL",
    "S CAPORAL",
    "ROOKIE",
    "RC",
    "RCA",
    "INSERT",
    "PARALLEL",
    "REFRACTOR",
    "CHROME",
    "PRIZM",
    "HOLO",
    "HOLOGRAPHIC",
    "SHORT PRINT",
    "SP",
    "SUPER SHORT PRINT",
    "SSP",
  ],

  /**
   * --------------------------------------------------------
   * Minimal metadata exclusions
   *
   * IMPORTANT:
   *
   * This is intentionally NOT a giant player blacklist.
   * --------------------------------------------------------
   */

  excludedNameTerms: [
    "BASEBALL",
    "BASKETBALL",
    "FOOTBALL",
    "HOCKEY",
    "PORTRAIT",
    "CARD",
    "SERIAL",
    "NUMBER",
    "ALLEN",
    "GINTER",
    "TOPPS",
    "BOWMAN",
    "DONRUSS",
    "FLEER",
    "UPPER",
    "DECK",
    "PSA",
    "SGC",
    "CGC",
    "BGS",
    "BECKETT",
    "GOOD",
    "GOOD HI",
    "PORTRAIT RED",
  ],

  /**
   * --------------------------------------------------------
   * OCR corrections
   * --------------------------------------------------------
   */

  ocrCorrections: [
    {
      pattern: /\bT\s*[\*7I|]?\s*7206\b/gi,
      replacement: "T206",
    },

    {
      pattern: /\bT\s*[\*7I|]?\s*206\b/gi,
      replacement: "T206",
    },

    {
      pattern: /\bALLEN\s*&\s*GINTEI\b/gi,
      replacement: "ALLEN & GINTER",
    },

    {
      pattern: /\bALLEN\s+8\s+GINTEI\b/gi,
      replacement: "ALLEN & GINTER",
    },

    {
      pattern: /\bALLEN\s+AND\s+GINTER\b/gi,
      replacement: "ALLEN & GINTER",
    },
  ],
};

/**
 * ============================================================
 * MAIN ENTRY POINT
 * ============================================================
 */

export function parseOCR(ocrResult) {
  if (!ocrResult?.regions) {
    console.warn("HobbliVision Parser: no OCR results supplied.");

    return createEmptyResult();
  }

  /**
   * --------------------------------------------------------
   * RAW OCR
   * --------------------------------------------------------
   */

  const rawOCR = collectRawOCR(ocrResult);
  const combinedText = rawOCR.map((entry) => entry.text).join("\n");

  /**
   * --------------------------------------------------------
   * NORMALIZATION
   * --------------------------------------------------------
   */

  const normalizedText = normalizeText(combinedText);

  /**
   * --------------------------------------------------------
   * OCR CORRECTION
   * --------------------------------------------------------
   */

  const correctedText = correctOCRText(normalizedText);

  /**
   * --------------------------------------------------------
   * CARD NUMBER
   * --------------------------------------------------------
   */

  const cardNumber = extractCardNumber(correctedText, normalizedText);

  /**
   * --------------------------------------------------------
   * SET
   * --------------------------------------------------------
   */

  const setResult = extractSet(correctedText);

  /**
   * --------------------------------------------------------
   * YEAR
   * --------------------------------------------------------
   */

  const yearResult = extractYear(correctedText);

  /**
   * --------------------------------------------------------
   * VARIATIONS
   * --------------------------------------------------------
   */

  const variationResults = extractVariationClues(correctedText);

  /**
   * --------------------------------------------------------
   * NAME CANDIDATES
   * --------------------------------------------------------
   */

  const nameCandidates = extractNameCandidates(
    rawOCR,
    correctedText,
    setResult,
  );

  /**
   * --------------------------------------------------------
   * IDENTITY CANDIDATES
   * --------------------------------------------------------
   */

  const identityCandidates = buildIdentityCandidates(
    nameCandidates,
    rawOCR,
    correctedText,
    setResult,
    cardNumber,
    yearResult,
  );

  const selectedIdentity = identityCandidates[0] || null;

  /**
   * --------------------------------------------------------
   * KEYWORDS
   * --------------------------------------------------------
   */

  const keywords = extractKeywords(
    correctedText,
    setResult,
    variationResults,
    cardNumber,
  );

  /**
   * --------------------------------------------------------
   * CANDIDATE ENTITIES
   * --------------------------------------------------------
   */

  const candidateEntities = buildCandidateEntities(
    correctedText,
    identityCandidates,
    keywords,
  );

  /**
   * --------------------------------------------------------
   * CLUES
   * --------------------------------------------------------
   */

  const clues = [];

  if (cardNumber) {
    clues.push({
      type: "cardNumber",
      value: cardNumber.value,
      confidence: cardNumber.confidence,
      source: "parser",
    });
  }

  if (setResult) {
    clues.push({
      type: "set",
      value: setResult.value,
      confidence: setResult.confidence,
      source: "parser",
    });
  }

  if (yearResult) {
    clues.push({
      type: "year",
      value: yearResult.value,
      confidence: yearResult.confidence,
      source: "parser",
    });
  }

  variationResults.forEach((variation) => {
    clues.push({
      type: "variation",
      value: variation.value,
      confidence: variation.confidence,
      source: "parser",
    });
  });

  identityCandidates.slice(0, 10).forEach((candidate) => {
    clues.push({
      type: "name",
      value: candidate.value,
      confidence: candidate.confidence,
      source: "identity",
    });
  });

  keywords.forEach((keyword) => {
    clues.push({
      type: "keyword",
      value: keyword.value,
      confidence: keyword.confidence,
      source: "parser",
    });
  });

  /**
   * --------------------------------------------------------
   * DIAGNOSTICS
   * --------------------------------------------------------
   */

  const diagnostics = {
    parserVersion: PARSER_VERSION,
    observationCount: rawOCR.length,
    candidateCount: candidateEntities.length,
    identityCandidateCount: identityCandidates.length,
    selectedIdentity: selectedIdentity,
    selectedSet: setResult,
    selectedCardNumber: cardNumber,
    selectedYear: yearResult,
    selectedVariations: variationResults,
    correctionCount: countCorrections(normalizedText, correctedText),
    regionSummary: summarizeRegions(rawOCR),
    nameDiagnostics: buildNameDiagnostics(nameCandidates, identityCandidates),
  };

  /**
   * --------------------------------------------------------
   * FINAL RESULT
   * --------------------------------------------------------
   */

  return {
    version: PARSER_VERSION,
    rawOCR,
    combinedText,
    normalizedText,
    correctedText,
    regions: buildRegionResults(rawOCR),
    cardNumber: cardNumber?.value || null,
    cardNumberConfidence: cardNumber?.confidence || 0,
    player: selectedIdentity?.value || null,
    playerConfidence: selectedIdentity?.confidence || 0,
    itemName: selectedIdentity?.value || null,
    itemNameConfidence: selectedIdentity?.confidence || 0,
    set: setResult?.value || null,
    setConfidence: setResult?.confidence || 0,
    year: yearResult?.value || null,
    yearConfidence: yearResult?.confidence || 0,
    variations: variationResults,
    nameCandidates,
    itemNameCandidates: identityCandidates,
    identityCandidates,
    candidateEntities,
    keywords: keywords.map((keyword) => keyword.value),
    clues,
    diagnostics,
    createdAt: new Date().toISOString(),
  };
}

/**
 * ============================================================
 * RAW OCR COLLECTION
 * ============================================================
 */

function collectRawOCR(ocrResult) {
  const rawOCR = [];

  Object.entries(ocrResult.regions || {}).forEach(([regionName, variants]) => {
    if (!variants) {
      return;
    }

    /**
     * Handle the normal structure:
     *
     * region -> variant -> result
     */

    Object.entries(variants).forEach(([variantName, result]) => {
      if (!result) {
        return;
      }

      /**
       * Some OCR implementations may return
       * text in slightly different forms.
       */

      let text = result.text;

      if (Array.isArray(text)) {
        text = text.join("\n");
      }

      if (typeof text !== "string") {
        return;
      }

      text = text.trim();

      if (!text) {
        return;
      }

      rawOCR.push({
        region: regionName,

        variant: variantName,

        text,

        confidence: normalizeOCRConfidence(result.confidence),
      });
    });
  });

  return rawOCR;
}

/**
 * ============================================================
 * OCR CONFIDENCE NORMALIZATION
 * ============================================================
 */

function normalizeOCRConfidence(confidence) {
  if (confidence === null || confidence === undefined) {
    return 0;
  }

  const numeric = Number(confidence);

  if (!Number.isFinite(numeric)) {
    return 0;
  }

  /**
   * Support both:
   *
   * 0.0 - 1.0
   *
   * and
   *
   * 0 - 100
   */

  if (numeric > 1) {
    return Math.min(1, numeric / 100);
  }

  return Math.max(0, Math.min(1, numeric));
}

/**
 * ============================================================
 * REGION RESULTS
 * ============================================================
 */

function buildRegionResults(rawOCR) {
  const regions = {};

  rawOCR.forEach((entry) => {
    if (!regions[entry.region]) {
      regions[entry.region] = {};
    }

    regions[entry.region][entry.variant] = {
      text: entry.text,

      confidence: entry.confidence,
    };
  });

  return regions;
}

/**
 * ============================================================
 * NORMALIZATION
 * ============================================================
 */

function normalizeText(text) {
  if (!text) {
    return "";
  }

  return String(text)
    .replace(/\r\n?/g, "\n")
    .replace(/[^\S\n]+/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * ============================================================
 * OCR CORRECTION
 * ============================================================
 */

function correctOCRText(text) {
  let corrected = text;

  KNOWLEDGE.ocrCorrections.forEach((rule) => {
    corrected = corrected.replace(rule.pattern, rule.replacement);
  });

  /**
   * Standalone 7206 is only converted when it looks like
   * a T206 identifier.
   */

  corrected = corrected.replace(/\b(?:#\s*)?7206\b/gi, "T206");

  return corrected;
}

/**
 * ============================================================
 * CARD NUMBER
 * ============================================================
 */

function extractCardNumber(correctedText, originalText) {
  /**
   * T206
   */

  const t206Match = correctedText.match(/\bT\s*206\b/i);

  if (t206Match) {
    return {
      value: "T206",
      confidence: calculateRepeatedPatternConfidence(
        correctedText,
        /\bT\s*206\b/gi,
        0.92,
      ),
    };
  }

  /**
   * #350
   */

  const hashMatch = correctedText.match(/#\s*(\d{1,4})\b/);

  if (hashMatch) {
    return {
      value: hashMatch[1],
      confidence: 0.78,
    };
  }

  /**
   * 350/460
   */

  const slashMatch = correctedText.match(/\b(\d{1,4})\s*\/\s*(\d{1,4})\b/);

  if (slashMatch) {
    return {
      value: `${slashMatch[1]}/${slashMatch[2]}`,

      confidence: 0.72,
    };
  }

  /**
   * Original OCR fallback.
   */

  const originalT206 = originalText.match(/\bT\s*[\*7I|]?\s*206\b/i);

  if (originalT206) {
    return {
      value: "T206",

      confidence: 0.9,
    };
  }

  return null;
}

/**
 * ============================================================
 * SET EXTRACTION
 * ============================================================
 */

function extractSet(text) {
  const upper = text.toUpperCase();

  const matches = [];

  KNOWLEDGE.sets.forEach((set) => {
    set.aliases.forEach((alias) => {
      const escaped = escapeRegExp(alias);

      const regex = new RegExp(`\\b${escaped}\\b`, "i");

      if (regex.test(upper)) {
        matches.push({
          value: set.canonical,

          confidence: set.confidence,

          alias,
        });
      }
    });
  });

  if (!matches.length) {
    return null;
  }

  matches.sort((a, b) => {
    const aExact = a.alias === a.value ? 0.05 : 0;
    const bExact = b.alias === b.value ? 0.05 : 0;

    return b.confidence + bExact - (a.confidence + aExact);
  });

  return {
    value: matches[0].value,

    confidence: Math.min(
      1,
      matches[0].confidence + Math.min(0.05, (matches.length - 1) * 0.015),
    ),

    observations: matches.length,
  };
}

/**
 * ============================================================
 * YEAR
 * ============================================================
 */

function extractYear(text) {
  const matches = text.match(/\b(18\d{2}|19\d{2}|20\d{2})\b/g);

  if (!matches?.length) {
    return null;
  }

  const counts = {};

  matches.forEach((year) => {
    counts[year] = (counts[year] || 0) + 1;
  });

  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];

  return {
    value: best[0],

    confidence: Math.min(0.9, 0.55 + best[1] * 0.08),
  };
}

/**
 * ============================================================
 * VARIATION CLUES
 * ============================================================
 */

function extractVariationClues(text) {
  const upper = text.toUpperCase();

  const results = [];

  KNOWLEDGE.variationTerms.forEach((term) => {
    const escaped = escapeRegExp(term);

    const regex = new RegExp(`\\b${escaped}\\b`, "i");

    if (!regex.test(upper)) {
      return;
    }

    results.push({
      value: term,

      confidence: getVariationConfidence(term),
    });
  });

  return [...new Map(results.map((result) => [result.value, result])).values()];
}
function getVariationConfidence(term) {
  if (term === "PORTRAIT-RED") {
    return 0.92;
  }
  if (term === "S.CAPORAL") {
    return 0.9;
  }
  if (term === "CAPORAL") {
    return 0.78;
  }
  if (term === "PORTRAIT") {
    return 0.72;
  }
  return 0.65;
}

/**
 * ============================================================
 * NAME CANDIDATES
 * ============================================================
 *
 * V4.3 CHANGE:
 *
 * We intentionally preserve more plausible name observations.
 *
 * A parser should not throw away "TY COBB" merely because
 * another OCR pass produced something like "FIT P A".
 *
 * The matcher can decide between candidates later.
 * ============================================================
 */

function extractNameCandidates(rawOCR, correctedText, setResult) {
  const observations = [];

  /**
   * --------------------------------------------------------
   * Every OCR observation independently
   * --------------------------------------------------------
   */

  rawOCR.forEach((entry) => {
    const lines = entry.text
      .split(/\n/)
      .map((line) => normalizeNameLine(line))
      .filter(Boolean);

    lines.forEach((line) => {
      const candidate = analyzeNameLine(line, entry, setResult);

      if (candidate) {
        observations.push(candidate);
      }
    });
  });

  /**
   * --------------------------------------------------------
   * Corrected combined OCR
   * --------------------------------------------------------
   */

  correctedText.split(/\n/).forEach((line) => {
    const normalized = normalizeNameLine(line);

    if (!normalized) {
      return;
    }

    const candidate = analyzeNameLine(
      normalized,
      {
        region: "combined",

        variant: "corrected",

        confidence: 0.5,
      },
      setResult,
    );

    if (candidate) {
      observations.push(candidate);
    }
  });

  /**
   * --------------------------------------------------------
   * Merge
   * --------------------------------------------------------
   */

  return mergeNameObservations(observations);
}

/**
 * ============================================================
 * NAME LINE ANALYSIS
 * ============================================================
 */

function analyzeNameLine(line, source, setResult) {
  if (!line || line.length < 3 || line.length > 42) {
    return null;
  }

  const upper = line.toUpperCase();

  if (isExcludedNameLine(upper)) {
    return null;
  }

  /**
   * --------------------------------------------------------
   * Remove obvious leading OCR garbage, but don't destroy
   * short historical names.
   * --------------------------------------------------------
   */

  const cleaned = cleanNameCandidate(line);

  if (!cleaned) {
    return null;
  }

  if (
    setResult &&
    normalizeIdentityText(cleaned) === normalizeIdentityText(setResult.value)
  ) {
    return null;
  }

  const tokens = cleaned.split(/\s+/);

  if (tokens.length > 5) {
    return null;
  }

  if (tokens.length === 1 && isGarbageNameToken(tokens[0])) {
    return null;
  }

  const letters = (cleaned.match(/[A-Za-z]/g) || []).length;

  const numbers = (cleaned.match(/[0-9]/g) || []).length;

  if (letters < 2) {
    return null;
  }

  if (numbers > letters) {
    return null;
  }

  /**
   * V4.3:
   *
   * Require reasonable alphabetic density, but permit
   * punctuation and initials.
   */

  const alphaRatio = letters / Math.max(1, cleaned.length);

  if (alphaRatio < 0.45) {
    return null;
  }

  const confidence = calculateNameObservationConfidence(cleaned, source);

  return {
    value: cleaned,

    normalized: normalizeIdentityText(cleaned),

    confidence,

    region: source.region,

    variant: source.variant,

    evidence: [
      {
        region: source.region,

        variant: source.variant,

        confidence: source.confidence,
      },
    ],
  };
}

/**
 * ============================================================
 * NAME LINE CLEANING
 * ============================================================
 */

function cleanNameCandidate(line) {
  let value = line

    /**
     * Remove leading symbols while preserving letters.
     */

    .replace(/^[^A-Za-z]+/, "")

    .replace(/^(?:NAME|PLAYER|SUBJECT|ATHLETE)\s*[:#-]\s*/i, "")

    .replace(/^(?:[A-Z]{1,3}\s*)?\d{1,4}\s*/i, "")

    .replace(
      /^(?:ALLEN\s*(?:&|AND)?\s*GINTER|TOPPS|BOWMAN|DONRUSS|FLEER|UPPER\s+DECK)\s*/i,
      "",
    )

    /**
     * Remove trailing OCR punctuation/garbage.
     */

    .replace(/[^A-Za-z.'\-&\s]+$/g, "")

    .replace(/\s+/g, " ")

    .trim();

  if (!value) {
    return null;
  }

  let tokens = value.split(/\s+/);

  /**
   * Remove known metadata only when it appears as a
   * trailing token.
   *
   * This is much safer than rejecting the entire line.
   */

  while (tokens.length > 2 && isGarbageNameToken(tokens[tokens.length - 1])) {
    tokens.pop();
  }

  /**
   * V4.3:
   *
   * Explicitly protect short legitimate names.
   *
   * Examples:
   *
   * TY COBB
   * JOE JACKSON
   * CY YOUNG
   * AL KALINE
   */

  value = tokens.join(" ");

  if (value.length < 3) {
    return null;
  }

  return value;
}

/**
 * ============================================================
 * NAME EXCLUSIONS
 * ============================================================
 */

function isExcludedNameLine(upper) {
  /**
   * Exact metadata.
   */

  if (
    KNOWLEDGE.excludedNameTerms.includes(upper) ||
    KNOWLEDGE.excludedNameTerms.includes(upper.replace(/\s+/g, " ").trim())
  ) {
    return true;
  }

  /**
   * Obvious grading labels.
   */

  if (/^(PSA|SGC|CGC|BGS|BECKETT)\b/.test(upper)) {
    return true;
  }

  /**
   * Obvious variation metadata.
   */

  if (
    upper.includes("PORTRAIT-RED") ||
    upper === "PORTRAIT" ||
    upper === "CAPORAL" ||
    upper === "S.CAPORAL"
  ) {
    return true;
  }

  /**
   * Entirely numeric / metadata-like.
   */

  const letters = (upper.match(/[A-Z]/g) || []).length;

  const numbers = (upper.match(/[0-9]/g) || []).length;

  if (numbers > letters) {
    return true;
  }

  /**
   * Set names by themselves should not become players.
   */

  for (const set of KNOWLEDGE.sets) {
    if (
      set.aliases.some(
        (alias) =>
          normalizeIdentityText(upper) === normalizeIdentityText(alias),
      )
    ) {
      return true;
    }
  }

  return false;
}

/**
 * ============================================================
 * GARBAGE NAME TOKEN
 * ============================================================
 */

function isGarbageNameToken(token) {
  const upper = token.toUpperCase();

  return (
    KNOWLEDGE.excludedNameTerms.includes(upper) ||
    upper.length <= 1 ||
    /^[A-Z]{1,2}[0-9]+$/.test(upper) ||
    /^[0-9]+$/.test(upper) ||
    (upper.length >= 6 && /([A-Z]{1,2})\1{2,}/.test(upper)) ||
    [
      "GOOD",
      "HI",
      "HIE",
      "IL",
      "HEH",
      "MINH",
      "BIH",
      "PR",
      "I",
      "II",
    ].includes(upper)
  );
}

/**
 * ============================================================
 * NAME OBSERVATION CONFIDENCE
 * ============================================================
 */

function calculateNameObservationConfidence(value, source) {
  let confidence = 0.35;

  const tokens = value.split(/\s+/);

  /**
   * Two-word names are especially valuable.
   *
   * This is important for names such as:
   *
   * TY COBB
   * BABE RUTH
   * CY YOUNG
   */

  if (tokens.length === 2) {
    confidence += 0.22;
  } else if (tokens.length === 3) {
    confidence += 0.18;
  } else if (tokens.length === 1) {
    confidence += 0.05;
  }

  /**
   * Mostly alphabetic.
   */

  const letters = (value.match(/[A-Za-z]/g) || []).length;

  if (letters / Math.max(1, value.length) > 0.8) {
    confidence += 0.18;
  }

  /**
   * OCR source confidence.
   *
   * Source confidence is normalized to 0-1.
   */

  confidence += Math.min(0.15, (source.confidence || 0) * 0.15);

  /**
   * Top region is strong evidence.
   */

  if (source.region === "top") {
    confidence += 0.12;
  }

  /**
   * Middle/bottom are still useful.
   */

  if (source.region === "bottom") {
    confidence += 0.05;
  }

  return Math.min(1, confidence);
}

/**
 * ============================================================
 * MERGE NAME OBSERVATIONS
 * ============================================================
 */

function mergeNameObservations(observations) {
  const groups = [];

  observations.forEach((observation) => {
    let bestGroup = null;

    let bestSimilarity = 0;

    groups.forEach((group) => {
      const similarity = identitySimilarity(
        observation.normalized,
        group.normalized,
      );

      if (similarity > bestSimilarity) {
        bestSimilarity = similarity;

        bestGroup = group;
      }
    });

    /**
     * V4.3:
     *
     * Similarity threshold remains conservative.
     *
     * This prevents unrelated short names/fragments
     * from being swallowed into one group.
     */

    if (bestGroup && bestSimilarity >= 0.75) {
      bestGroup.observations += 1;

      bestGroup.totalConfidence += observation.confidence;

      bestGroup.evidence.push(...observation.evidence);

      /**
       * Prefer the cleaner representation.
       */

      if (scoreNameShape(observation.value) > scoreNameShape(bestGroup.value)) {
        bestGroup.value = observation.value;

        bestGroup.normalized = observation.normalized;
      }
    } else {
      groups.push({
        value: observation.value,

        normalized: observation.normalized,

        observations: 1,

        totalConfidence: observation.confidence,

        evidence: [...observation.evidence],
      });
    }
  });

  return groups

    .map((group) => {
      const repetitionBonus = Math.min(0.3, (group.observations - 1) * 0.055);

      const averageConfidence = group.totalConfidence / group.observations;

      const topEvidence = group.evidence.filter(
        (evidence) => evidence.region === "top",
      ).length;

      const topBonus = Math.min(0.15, topEvidence * 0.025);

      const confidence = Math.min(
        1,
        averageConfidence + repetitionBonus + topBonus,
      );

      return {
        value: group.value,

        normalized: group.normalized,

        observations: group.observations,

        exactObservations: countExactObservations(group),

        confidence,

        evidence: group.evidence,
      };
    })

    .sort((a, b) => b.confidence - a.confidence)

    .slice(0, 50);
}

/**
 * ============================================================
 * IDENTITY CANDIDATES
 * ============================================================
 *
 * This is where 4.3 puts more emphasis on preserving legitimate
 * identity candidates instead of aggressively selecting one.
 * ============================================================
 */

function buildIdentityCandidates(
  nameCandidates,
  rawOCR,
  correctedText,
  setResult,
  cardNumber,
  yearResult,
) {
  const candidates = nameCandidates.map((candidate) => {
    let score = candidate.confidence;

    /**
     * Repetition.
     */

    const repetition = Math.min(0.2, candidate.observations * 0.025);

    score += repetition;

    /**
     * Exact observations.
     */

    const exactObservation = Math.min(
      0.15,
      candidate.exactObservations * 0.035,
    );

    score += exactObservation;

    /**
     * Top region.
     */

    const topEvidence = candidate.evidence.filter(
      (evidence) => evidence.region === "top",
    ).length;

    const topRegion = Math.min(0.2, topEvidence * 0.035);

    score += topRegion;

    const averageSourceConfidence =
      candidate.evidence.reduce(
        (total, evidence) => total + evidence.confidence,
        0,
      ) / Math.max(1, candidate.evidence.length);

    const sourceQuality = Math.min(0.12, averageSourceConfidence * 0.3);

    score += sourceQuality;

    const lowConfidencePenalty =
      averageSourceConfidence < 0.25 ? 0.08 : 0;

    score -= lowConfidencePenalty;

    /**
     * Set support.
     */

    const setSupport = setResult ? 0.04 : 0;

    score += setSupport;

    /**
     * Year support.
     *
     * A recognized year means the OCR contains
     * card-identification information, which slightly
     * increases the usefulness of nearby name text.
     */

    const yearSupport = yearResult ? 0.025 : 0;

    score += yearSupport;

    /**
     * Card number support.
     */

    const cardNumberSupport = cardNumber ? 0.025 : 0;

    score += cardNumberSupport;

    /**
     * Name shape.
     */

    const shape = Math.min(0.1, scoreNameShape(candidate.value) * 0.1);

    score += shape;

    const caseQuality = scoreNameCase(candidate.value);

    score += caseQuality;

    const shortTrailingTokenPenalty = scoreShortTrailingTokens(
      candidate.value,
    );

    score -= shortTrailingTokenPenalty;

    /**
     * Garbage penalty.
     */

    const penalty = garbagePenalty(candidate.value);
    score -= penalty;

    /**
     * V4.3 rescue bonus.
     * Clean two-token names are valuable, especially
     * in older cards where names may be short.
     */

    const rescue = cleanTwoWordNameBonus(candidate.value);

    score += rescue;

    return {
      ...candidate,

      confidence: clamp(score, 0, 1),
      rankingScore: score,

      scoreBreakdown: {
        base: candidate.confidence,
        repetition,
        exactObservation,
        topRegion,
        sourceQuality,
        lowConfidencePenalty,
        setSupport,
        yearSupport,
        cardNumberSupport,
        shape,
        caseQuality,
        shortTrailingTokenPenalty,
        cleanTwoWordName: rescue,
        garbagePenalty: penalty,
      },
    };
  });

  /**
   * --------------------------------------------------------
   * Final sort
   * --------------------------------------------------------
   */

  return candidates

    .sort((a, b) => {
      /**
       * Confidence is primary.
       */

      const confidenceDifference =
        (b.rankingScore ?? b.confidence) -
        (a.rankingScore ?? a.confidence);

      if (Math.abs(confidenceDifference) > 0.015) {
        return confidenceDifference;
      }

      /**
       * If candidates are close, favor:
       *
       * 1. more observations
       * 2. more exact observations
       * 3. top-region evidence
       */

      const aEvidence =
        (a.observations || 0) +
        (a.exactObservations || 0) * 1.5 +
        countTopEvidence(a) * 1.25;

      const bEvidence =
        (b.observations || 0) +
        (b.exactObservations || 0) * 1.5 +
        countTopEvidence(b) * 1.25;

      return bEvidence - aEvidence;
    })

    .slice(0, 30);
}

/**
 * ============================================================
 * CLEAN TWO-WORD NAME BONUS
 * ============================================================
 *
 * This is deliberately generic.
 *
 * It does NOT know that TY COBB is a player.
 *
 * It simply recognizes that a clean two-word alphabetic
 * candidate is structurally valuable.
 * ============================================================
 */

function cleanTwoWordNameBonus(value) {
  const tokens = value.split(/\s+/);

  if (tokens.length !== 2) {
    return 0;
  }

  if (tokens.some((token) => !/^[A-Za-z.'-]+$/.test(token))) {
    return 0;
  }

  if (tokens.some((token) => token.length < 2)) {
    return 0;
  }

  return 0.06;
}

function scoreNameCase(value) {
  const tokens = value.split(/\s+/).filter(Boolean);

  if (!tokens.length) {
    return 0;
  }

  const isUppercase = tokens.every((token) => /^[A-Z.'-]+$/.test(token));
  const isTitleCase = tokens.every((token) => /^[A-Z][a-z.'-]*$/.test(token));

  if (isUppercase) {
    return 0.16;
  }

  return isTitleCase ? 0.04 : 0;
}

function scoreShortTrailingTokens(value) {
  const tokens = value.split(/\s+/).filter(Boolean);

  return tokens.slice(1).some((token) => /^[A-Za-z]{2}$/.test(token))
    ? 0.12
    : 0;
}

/**
 * ============================================================
 * IDENTITY SIMILARITY
 * ============================================================
 */

function identitySimilarity(a, b) {
  if (!a || !b) {
    return 0;
  }

  if (a === b) {
    return 1;
  }

  const aTokens = a.split(/\s+/);

  const bTokens = b.split(/\s+/);

  let matched = 0;

  aTokens.forEach((tokenA) => {
    let best = 0;

    bTokens.forEach((tokenB) => {
      best = Math.max(best, tokenSimilarity(tokenA, tokenB));
    });

    if (best >= 0.72) {
      matched++;
    }
  });

  const denominator = Math.max(aTokens.length, bTokens.length);

  return matched / denominator;
}

/**
 * ============================================================
 * TOKEN SIMILARITY
 * ============================================================
 */

function tokenSimilarity(a, b) {
  if (a === b) {
    return 1;
  }

  if (!a || !b) {
    return 0;
  }

  const distance = levenshteinDistance(a, b);

  if (Math.max(a.length, b.length) <= 2 && distance === 1) {
    return 0.75;
  }

  return 1 - distance / Math.max(a.length, b.length);
}

/**
 * ============================================================
 * LEVENSHTEIN
 * ============================================================
 */

function levenshteinDistance(a, b) {
  const matrix = Array.from(
    {
      length: b.length + 1,
    },
    () => Array(a.length + 1).fill(0),
  );

  for (let i = 0; i <= a.length; i++) {
    matrix[0][i] = i;
  }

  for (let j = 0; j <= b.length; j++) {
    matrix[j][0] = j;
  }

  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;

      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,

        matrix[j - 1][i] + 1,

        matrix[j - 1][i - 1] + cost,
      );
    }
  }

  return matrix[b.length][a.length];
}

/**
 * ============================================================
 * NAME SHAPE
 * ============================================================
 */

function scoreNameShape(value) {
  const tokens = value.split(/\s+/);

  if (tokens.length === 2) {
    return 1;
  }

  if (tokens.length === 3) {
    return 0.95;
  }

  if (tokens.length === 1) {
    return 0.55;
  }

  if (tokens.length === 4) {
    return 0.5;
  }

  return 0.2;
}

/**
 * ============================================================
 * GARBAGE PENALTY
 * ============================================================
 */

function garbagePenalty(value) {
  const upper = value.toUpperCase();

  let penalty = 0;

  /**
   * These are deliberately weak penalties.
   *
   * They should not completely erase a candidate.
   */

  const garbageWords = [
    "GOOD",
    "HI",
    "HIE",
    "BIH",
    "MINH",
    "RERUN",
    "TARE",
    "IRIE",
    "SERN",
    "AERA",
    "CREE",
    "POTS",
    "SAVOY",
  ];

  garbageWords.forEach((word) => {
    const regex = new RegExp(`\\b${escapeRegExp(word)}\\b`, "i");

    if (regex.test(upper)) {
      penalty += 0.08;
    }
  });

  /**
   * Strange character composition.
   */

  if (/[^A-Za-z.'\- ]/.test(value)) {
    penalty += 0.05;
  }

  return Math.min(0.3, penalty);
}

/**
 * ============================================================
 * CANDIDATE ENTITIES
 * ============================================================
 */

function buildCandidateEntities(text, identityCandidates, keywords) {
  const entities = [];

  /**
   * Identity candidates first.
   */

  identityCandidates.forEach((candidate) => {
    entities.push({
      type: "identity",

      value: candidate.value,

      normalized: candidate.normalized,

      confidence: candidate.confidence,

      observations: candidate.observations,

      evidence: candidate.evidence,
    });
  });

  /**
   * Keywords.
   */

  keywords.forEach((keyword) => {
    entities.push({
      type: "keyword",

      value: keyword.value,

      normalized: normalizeIdentityText(keyword.value),

      confidence: keyword.confidence,

      observations: keyword.observations || 1,

      evidence: [],
    });
  });

  /**
   * Remaining repeated words.
   */

  const words = text.toUpperCase().match(/\b[A-Z][A-Z.'\-]{2,}\b/g) || [];

  const counts = {};

  words.forEach((word) => {
    if (KNOWLEDGE.excludedNameTerms.includes(word)) {
      return;
    }

    counts[word] = (counts[word] || 0) + 1;
  });

  Object.entries(counts).forEach(([word, count]) => {
    if (count < 2) {
      return;
    }

    entities.push({
      type: "word",

      value: word,

      normalized: word,

      confidence: Math.min(0.65, 0.35 + count * 0.05),

      observations: count,

      evidence: [],
    });
  });

  /**
   * Deduplicate.
   */

  const unique = new Map();

  entities.forEach((entity) => {
    const key = `${entity.type}:${entity.normalized}`;

    const existing = unique.get(key);

    if (!existing || entity.confidence > existing.confidence) {
      unique.set(key, entity);
    }
  });

  return [...unique.values()]
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 100);
}

/**
 * ============================================================
 * KEYWORDS
 * ============================================================
 */

function extractKeywords(text, setResult, variationResults, cardNumber) {
  const results = [];

  if (setResult) {
    results.push({
      value: setResult.value,

      confidence: setResult.confidence,

      observations: setResult.observations,
    });
  }

  if (cardNumber) {
    results.push({
      value: cardNumber.value,

      confidence: cardNumber.confidence,

      observations: 1,
    });
  }

  variationResults.forEach((variation) => {
    const occurrences = countOccurrences(text, variation.value);

    results.push({
      value: variation.value,

      confidence: Math.min(
        1,
        variation.confidence + Math.min(0.08, occurrences * 0.015),
      ),

      observations: occurrences,
    });
  });

  /**
   * Grading companies.
   */

  ["PSA", "SGC", "CGC", "BGS", "BECKETT"].forEach((term) => {
    const occurrences = countOccurrences(text, term);

    if (occurrences > 0) {
      results.push({
        value: term,

        confidence: 0.7,

        observations: occurrences,
      });
    }
  });

  return [...new Map(results.map((result) => [result.value, result])).values()];
}

/**
 * ============================================================
 * NAME DIAGNOSTICS
 * ============================================================
 */

function buildNameDiagnostics(nameCandidates, identityCandidates) {
  return {
    totalNameCandidates: nameCandidates.length,

    totalIdentityCandidates: identityCandidates.length,

    topNameCandidates: nameCandidates.slice(0, 10).map((candidate) => ({
      value: candidate.value,

      confidence: candidate.confidence,

      observations: candidate.observations,

      exactObservations: candidate.exactObservations,
    })),

    topIdentityCandidates: identityCandidates.slice(0, 10).map((candidate) => ({
      value: candidate.value,

      confidence: candidate.confidence,

      observations: candidate.observations,

      exactObservations: candidate.exactObservations,

      scoreBreakdown: candidate.scoreBreakdown,
    })),
  };
}

/**
 * ============================================================
 * SUPPORT FUNCTIONS
 * ============================================================
 */

function normalizeNameLine(line) {
  if (!line) {
    return "";
  }

  return line

    .replace(/[^\x20-\x7E]/g, " ")

    .replace(/^[^A-Za-z]+/, "")

    .replace(/\s+/g, " ")

    .trim();
}

function normalizeIdentityText(value) {
  return value

    .toUpperCase()

    .replace(/[^A-Z0-9 ]/g, " ")

    .replace(/\s+/g, " ")

    .trim();
}

function countExactObservations(group) {
  const counts = {};

  group.evidence.forEach((evidence) => {
    const key = `${evidence.region}:${evidence.variant}`;

    counts[key] = (counts[key] || 0) + 1;
  });

  return Math.max(1, Object.keys(counts).length);
}

function countTopEvidence(candidate) {
  return (
    candidate?.evidence?.filter((evidence) => evidence.region === "top")
      .length || 0
  );
}

function countOccurrences(text, term) {
  if (!text || !term) {
    return 0;
  }

  const escaped = escapeRegExp(term);

  const matches = text.match(new RegExp(escaped, "gi"));

  return matches?.length || 0;
}

function calculateRepeatedPatternConfidence(text, regex, base) {
  const matches = text.match(regex) || [];

  return Math.min(1, base + Math.min(0.08, (matches.length - 1) * 0.025));
}

function countCorrections(original, corrected) {
  if (original === corrected) {
    return 0;
  }

  let count = 0;

  KNOWLEDGE.ocrCorrections.forEach((rule) => {
    const matches = original.match(rule.pattern);

    if (matches) {
      count += matches.length;
    }
  });

  return count;
}

function summarizeRegions(rawOCR) {
  const summary = {};
  rawOCR.forEach((entry) => {
    if (!summary[entry.region]) {
      summary[entry.region] = {
        observations: 0,
        averageConfidence: 0,
      };
    }

    const region = summary[entry.region];
    region.observations++;
    region.averageConfidence += entry.confidence;
  });

  Object.values(summary).forEach((region) => {
    if (region.observations) {
      region.averageConfidence = Number(
        (region.averageConfidence / region.observations).toFixed(2),
      );
    }
  });
  return summary;
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

/**
 * ============================================================
 * EMPTY RESULT
 * ============================================================
 */

function createEmptyResult() {
  return {
    version: PARSER_VERSION,
    rawOCR: [],
    combinedText: "",
    normalizedText: "",
    correctedText: "",
    regions: {},
    cardNumber: null,
    cardNumberConfidence: 0,
    player: null,
    playerConfidence: 0,
    itemName: null,
    itemNameConfidence: 0,
    set: null,
    setConfidence: 0,
    year: null,
    yearConfidence: 0,
    variations: [],
    nameCandidates: [],
    itemNameCandidates: [],
    identityCandidates: [],
    candidateEntities: [],
    keywords: [],
    clues: [],
    diagnostics: {
      parserVersion: PARSER_VERSION,
      observationCount: 0,
      candidateCount: 0,
      identityCandidateCount: 0,
      selectedIdentity: null,
    },
    createdAt: new Date().toISOString(),
  };
}
